/**
 * EquilateralDashboard - Adaptive Dashboard Page
 *
 * Provides an adaptive experience based on client onboarding status:
 * - New clients (completion < 100%): Onboarding checklist + Quick actions
 * - Mature clients (completion = 100%): Health overview + Recommended actions
 *
 * Features:
 * - Adaptive rendering based on onboarding progress
 * - Real-time health scores from repository assessments
 * - Aggregated recommended actions from assessments
 * - Quick action handlers for common operations
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { Spinner, Alert } from 'flowbite-react';

// Components
import CPODashboardWidget from '../../components/CPODashboardWidget';
import RepositoryAssessmentsWidget from '../../components/RepositoryAssessmentsWidget';
import OnboardingChecklist from '../../components/OnboardingChecklist';
import HealthOverview, { type HealthData } from '../../components/HealthOverview';
import RecommendedActionsPanel, { type RecommendedAction } from '../../components/RecommendedActionsPanel';
import QuickActionsBar, {
  type QuickActionType,
  onboardingActions,
  matureClientActions,
} from '../../components/QuickActionsBar';

// API
import { API_ENDPOINTS } from '../../services/api/endpoints';
import { Make_Authorized_API_Call, isSuccessResponse } from '../../api/apiClient';
import {
  getOnboardingProgress,
  type OnboardingProgress,
  type OnboardingStep,
} from '../../api/onboarding';

// =============================================================================
// TYPES
// =============================================================================

interface Assessment {
  Assessment_ID: string;
  Repository: string;
  Status: string;
  Health_Score: number;
  Concerns_Count: number;
  Top_Concerns: string[];
  Created_At: string;
  Recommended_Actions: Array<{
    type: string;
    label: string;
    priority: 'high' | 'medium' | 'low';
    workflow: string;
    description: string;
  }>;
}

// =============================================================================
// COMPONENT
// =============================================================================

const EquilateralDashboard: React.FC = () => {
  const navigate = useNavigate();

  // Get client ID from localStorage (set by auth context)
  const clientId = localStorage.getItem('selectedClientId') || '';

  // State
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [onboardingProgress, setOnboardingProgress] = useState<OnboardingProgress | null>(null);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [healthData, setHealthData] = useState<HealthData | null>(null);

  // Determine if client is in onboarding mode
  const isOnboarding = useMemo(() => {
    if (!onboardingProgress) return true; // Default to onboarding while loading
    return onboardingProgress.completion_percentage < 100;
  }, [onboardingProgress]);

  // Aggregate recommended actions from all assessments
  const aggregatedActions = useMemo((): RecommendedAction[] => {
    const allActions: RecommendedAction[] = [];
    let actionIndex = 0;

    assessments.forEach(assessment => {
      assessment.Recommended_Actions?.forEach(action => {
        allActions.push({
          action_id: `${assessment.Assessment_ID}-${action.type}-${actionIndex++}`,
          type: action.type,
          label: action.label,
          priority: action.priority,
          workflow: action.workflow,
          description: action.description,
          repository: assessment.Repository,
          assessment_id: assessment.Assessment_ID,
        });
      });
    });

    // Sort by priority: high first, then medium, then low
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return allActions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority]);
  }, [assessments]);

  // =============================================================================
  // DATA FETCHING
  // =============================================================================

  const fetchOnboardingProgress = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await getOnboardingProgress(clientId);

      if (isSuccessResponse(response)) {
        const progress = response.data?.Records?.[0];
        setOnboardingProgress(progress || null);
      } else {
        // If endpoint doesn't exist yet, assume completed onboarding
        console.warn('[EquilateralDashboard] Onboarding progress endpoint not available, defaulting to mature client view');
        setOnboardingProgress({
          Client_ID: clientId,
          Client_Name: 'Client',
          completion_percentage: 100,
          total_steps: 0,
          completed_steps: 0,
          required_steps_completed: 0,
          total_required_steps: 0,
          steps: [],
          started_at: new Date().toISOString(),
          last_activity_at: null,
        });
      }
    } catch (err) {
      // If onboarding endpoint fails, default to mature client view
      console.warn('[EquilateralDashboard] Onboarding fetch failed, defaulting to mature view:', err);
      setOnboardingProgress({
        Client_ID: clientId,
        Client_Name: 'Client',
        completion_percentage: 100,
        total_steps: 0,
        completed_steps: 0,
        required_steps_completed: 0,
        total_required_steps: 0,
        steps: [],
        started_at: new Date().toISOString(),
        last_activity_at: null,
      });
    }
  }, [clientId]);

  const fetchAssessments = useCallback(async () => {
    if (!clientId) return;

    try {
      const response = await Make_Authorized_API_Call<Assessment>(
        API_ENDPOINTS.GITHUB_APP.ASSESSMENTS_LIST,
        'GET',
        undefined,
        { params: { Client_ID: clientId, limit: '10' } }
      );

      if (isSuccessResponse(response)) {
        setAssessments(response.data?.Records || []);
      }
    } catch (err) {
      console.warn('[EquilateralDashboard] Assessments fetch failed:', err);
    }
  }, [clientId]);

  const buildHealthData = useCallback((): HealthData => {
    // Calculate overall health from assessments - fail loud if no data
    const avgHealth = assessments.length > 0
      ? Math.round(assessments.reduce((sum, a) => sum + a.Health_Score, 0) / assessments.length)
      : 0; // No fallback - show 0 if no assessments

    // Trend requires historical data from API - show stable when unavailable
    // TODO: Fetch historical health data from /api/health/trend endpoint when available
    const trendChange = 0;

    return {
      overall: {
        overall_score: avgHealth,
        trend: 'stable', // Requires historical data - stable until API available
        trend_change: trendChange,
        last_updated: new Date().toISOString(),
      },
      repositories: assessments.map(a => ({
        repository: a.Repository,
        health_score: a.Health_Score,
        concerns_count: a.Concerns_Count || a.Top_Concerns?.length || 0,
        last_scan_at: a.Created_At,
      })),
      // Specifications data requires API: GET /api/specifications/stats
      // Showing zeros until endpoint is available - no fake data
      specifications: {
        total_specs: 0,
        approved_specs: 0,
        draft_specs: 0,
        outdated_specs: 0,
        avg_approval_time_days: 0,
      },
      // Workflows data requires API: GET /api/workflows/stats
      // Showing zeros until endpoint is available - no fake data
      workflows: {
        total_executions_today: 0,
        success_rate: 0,
        avg_execution_time_seconds: 0,
        failed_workflows: 0,
      },
    };
  }, [assessments]);

  // Load all data
  useEffect(() => {
    const loadData = async () => {
      if (!clientId) {
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        await Promise.all([
          fetchOnboardingProgress(),
          fetchAssessments(),
        ]);
      } catch (err) {
        console.error('[EquilateralDashboard] Error loading data:', err);
        setError('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [clientId, fetchOnboardingProgress, fetchAssessments]);

  // Build health data when assessments change
  useEffect(() => {
    if (!isOnboarding && assessments.length >= 0) {
      setHealthData(buildHealthData());
    }
  }, [isOnboarding, assessments, buildHealthData]);

  // =============================================================================
  // ACTION HANDLERS
  // =============================================================================

  const handleOnboardingAction = useCallback((step: OnboardingStep) => {
    console.log('[EquilateralDashboard] Onboarding action:', step.step_id, step.action_type, step.action_target);

    switch (step.action_type) {
      case 'link':
        navigate(step.action_target);
        break;
      case 'modal':
        // Modals would be opened via state - for now, navigate to related page
        if (step.action_target.includes('document')) {
          navigate('/documents');
        } else if (step.action_target.includes('repo')) {
          navigate('/repositories');
        } else {
          toast.success(`Opening: ${step.step_name}`);
        }
        break;
      case 'api_call':
        toast.success(`Triggering: ${step.step_name}`);
        // API calls would be made here
        break;
      default:
        navigate(step.action_target || '/');
    }
  }, [navigate]);

  const handleQuickAction = useCallback((actionId: QuickActionType) => {
    console.log('[EquilateralDashboard] Quick action:', actionId);

    switch (actionId) {
      case 'scan-repo':
      case 'connect-github':
        navigate('/repositories');
        break;
      case 'upload-docs':
        navigate('/documents');
        break;
      case 'create-spec':
        navigate('/specifications/new');
        break;
      case 'view-specs':
        navigate('/specifications');
        break;
      case 'run-workflow':
        toast.success('Opening workflow selector...');
        // Would open workflow modal
        break;
      case 'security-review':
        toast.success('Triggering security review...');
        // Would call security review API
        break;
      case 'invite-team':
        navigate('/settings/team');
        break;
      case 'view-reports':
        navigate('/assessments');
        break;
      case 'settings':
        navigate('/settings');
        break;
      default:
        toast.error(`Unknown action: ${actionId}`);
    }
  }, [navigate]);

  const handleTriggerRecommendedAction = useCallback(async (action: RecommendedAction) => {
    console.log('[EquilateralDashboard] Triggering recommended action:', action);
    toast.success(`Starting ${action.workflow} workflow for ${action.repository}...`);

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.AGENTS.EXECUTE,
        'POST',
        {
          workflow: action.workflow,
          repository: action.repository,
          Client_ID: clientId,
        }
      );

      if (isSuccessResponse(response)) {
        toast.success(`${action.workflow} workflow started successfully`);
      } else {
        toast.error(response.message || `Failed to start ${action.workflow} workflow`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start workflow';
      toast.error(errorMessage);
      console.error('[EquilateralDashboard] Workflow execution error:', err);
    }
  }, [clientId]);

  const handleTriggerWorkflow = useCallback(async (workflow: string, repository: string) => {
    toast.success(`Starting ${workflow} workflow for ${repository}...`);
    console.log('[EquilateralDashboard] Trigger workflow:', { workflow, repository });

    try {
      const response = await Make_Authorized_API_Call(
        API_ENDPOINTS.AGENTS.EXECUTE,
        'POST',
        {
          workflow,
          repository,
          Client_ID: clientId,
        }
      );

      if (isSuccessResponse(response)) {
        toast.success(`${workflow} workflow started successfully`);
      } else {
        toast.error(response.message || `Failed to start ${workflow} workflow`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start workflow';
      toast.error(errorMessage);
      console.error('[EquilateralDashboard] Workflow execution error:', err);
    }
  }, [clientId]);

  const handleViewAllAssessments = useCallback(() => {
    navigate('/assessments');
  }, [navigate]);

  // =============================================================================
  // RENDER: LOADING
  // =============================================================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Spinner size="xl" className="mb-4" />
          <p className="text-gray-500">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ERROR
  // =============================================================================

  if (error) {
    return (
      <div className="p-6">
        <Alert color="failure">
          <span className="font-medium">Error!</span> {error}
        </Alert>
      </div>
    );
  }

  // =============================================================================
  // RENDER: NO CLIENT CONTEXT
  // =============================================================================

  if (!clientId) {
    return (
      <div className="p-6">
        <Alert color="warning">
          <span className="font-medium">No client selected.</span> Please select a client to view the dashboard.
        </Alert>
      </div>
    );
  }

  // =============================================================================
  // RENDER: ONBOARDING VIEW (New Clients)
  // =============================================================================

  if (isOnboarding && onboardingProgress) {
    return (
      <div className="p-6 bg-gray-50 min-h-screen">
        <div className="max-w-5xl mx-auto space-y-6">
          {/* Onboarding Checklist */}
          <OnboardingChecklist
            progress={onboardingProgress}
            onActionClick={handleOnboardingAction}
          />

          {/* Quick Actions */}
          <QuickActionsBar
            onActionClick={handleQuickAction}
            visibleActions={onboardingActions}
          />

          {/* Repository Assessments (if any) */}
          {assessments.length > 0 && (
            <RepositoryAssessmentsWidget
              clientId={clientId}
              maxItems={3}
              onTriggerWorkflow={handleTriggerWorkflow}
              onViewAll={handleViewAllAssessments}
            />
          )}

          {/* CPO Widget (preview for onboarding users) */}
          {clientId && (
            <CPODashboardWidget
              clientId={clientId}
              onViewDetails={() => navigate('/specifications')}
              compact={true}
            />
          )}
        </div>
      </div>
    );
  }

  // =============================================================================
  // RENDER: MATURE CLIENT VIEW (Completed Onboarding)
  // =============================================================================

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="mb-2">
          <h1 className="text-2xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600">Platform health and recommended actions</p>
        </div>

        {/* Health Overview */}
        {healthData && (
          <HealthOverview
            health={healthData}
            onViewRepositories={() => navigate('/repositories')}
            onViewSpecifications={() => navigate('/specifications')}
            onViewWorkflows={() => navigate('/workflows')}
          />
        )}

        {/* Two Column Layout: Actions + Assessments */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Recommended Actions */}
          <RecommendedActionsPanel
            actions={aggregatedActions}
            onTriggerAction={handleTriggerRecommendedAction}
            onRefresh={() => {
              fetchAssessments();
              toast.success('Refreshing recommended actions...');
            }}
            maxVisible={8}
          />

          {/* Repository Assessments */}
          <RepositoryAssessmentsWidget
            clientId={clientId}
            maxItems={5}
            onTriggerWorkflow={handleTriggerWorkflow}
            onViewAll={handleViewAllAssessments}
          />
        </div>

        {/* Quick Actions */}
        <QuickActionsBar
          onActionClick={handleQuickAction}
          visibleActions={matureClientActions}
        />

        {/* CPO Dashboard Widget */}
        <CPODashboardWidget
          clientId={clientId}
          onViewDetails={() => navigate('/specifications')}
        />
      </div>
    </div>
  );
};

export default EquilateralDashboard;
